import { captureHealth } from '../lib/captureHealth';
import { noteError, noteEvent, patchDiagnostics } from '../lib/diagnostics';
import { posterFromCanvas } from '../lib/posterFrame';
import { buildCaptureStream } from './audio';
import { pickMimeType } from './mimeTypes';

/**
 * Clip recorder.
 *
 * Video comes from `captureStream()` on the AR module's canvas — which is 720x1280,
 * so the output is 9:16 with no cropping needed. Audio is our mic track, muxed
 * in here. The AR module is not involved in either.
 *
 * The recording window starts at the trigger and runs through the reveal, so the
 * transformation itself is in the clip. The transition is a DOM overlay and
 * would not appear in a canvas stream on its own, so the logo compositor draws
 * it into the recorded canvas — see logoComposite's setOverlay.
 *
 * arm() and start() are separate on purpose: everything costly happens in arm(),
 * which the capture stage calls during the live phase, leaving the trigger frame
 * to do almost nothing.
 */

export interface Clip {
  blob: Blob;
  mimeType: string;
  durationMs: number;
  sizeBytes: number;
  /**
   * The last frame of the take, as a JPEG data URL, so the result screen has a
   * cover image before it has a decoder. Null if the canvas would not give one
   * up; ResultScreen then falls back to reading a frame out of the blob.
   */
  posterUrl: string | null;
}

export interface RecorderOptions {
  canvas: HTMLCanvasElement;
  micStream: MediaStream | null;
  /** Hard stop, so a stuck flow cannot record forever. */
  maxDurationMs: number;
  fps?: number;
  videoBitsPerSecond?: number;
  /**
   * A capture stream the caller built and is sharing between recorders — see
   * PreRollRecorder. When set, this recorder records from it but does not stop
   * its tracks, because they are not its to stop.
   */
  stream?: MediaStream;
}

/**
 * A single chunk keeps it simple; the timeslice exists so a crash mid-clip
 * still leaves us something rather than nothing.
 */
const TIMESLICE_MS = 1000;

export class ClipRecorder {
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private stream: MediaStream | null = null;
  private startedAt = 0;
  private maxTimer: number | null = null;
  private settled: ((clip: Clip | null) => void) | null = null;
  private result: Promise<Clip | null> | null = null;

  private armed = false;
  /** False while recording from a stream someone else built and will release. */
  private ownsStream = true;
  /** Set by restart(), read by onstop, so a recycled session yields no clip. */
  private restarting = false;
  private maxDurationMs = 0;

  /**
   * Build the capture stream and construct the MediaRecorder, without recording.
   *
   * Split out of start() so the costly part can happen while the guest is still
   * being told to caw, rather than on the frame the effect fires — canvas
   * captureStream and MediaRecorder construction are the expensive, one-off half
   * and were landing exactly on the trigger. Safe to call more than once; only
   * the first does work.
   */
  arm(options: RecorderOptions): boolean {
    if (this.armed) return true;

    const mimeType = pickMimeType();
    if (!mimeType) {
      noteError('recorder', new Error('No supported MediaRecorder mime type'));
      return false;
    }

    try {
      this.ownsStream = !options.stream;
      this.stream =
        options.stream ??
        buildCaptureStream(options.canvas, options.micStream, options.fps ?? 30);
      this.recorder = new MediaRecorder(this.stream, {
        mimeType,
        // Conservative: the kiosk board is the performance floor and a fat
        // bitrate is the first thing to cost us frames.
        videoBitsPerSecond: options.videoBitsPerSecond ?? 4_000_000,
      });
    } catch (err) {
      noteError('recorder.construct', err);
      this.cleanupStream();
      return false;
    }

    this.armedMimeType = mimeType;
    this.armed = true;
    return true;
  }

  private armedMimeType: string | null = null;

  start(options: RecorderOptions): boolean {
    if (!this.arm(options)) return false;
    const recorder = this.recorder;
    const mimeType = this.armedMimeType;
    if (!recorder || !mimeType) return false;

    this.chunks = [];
    this.maxDurationMs = options.maxDurationMs;
    patchDiagnostics({ recorderMimeType: mimeType });

    this.result = new Promise<Clip | null>((resolve) => {
      this.settled = resolve;
    });

    recorder.ondataavailable = (e: BlobEvent) => {
      if (e.data && e.data.size > 0) this.chunks.push(e.data);
      // Chunk size is the one signal that says whether the encoder is being
      // handed a moving picture — see captureHealth.
      captureHealth.recorderChunk(e.data?.size ?? 0);
    };

    recorder.onerror = (e: Event) => {
      noteError('recorder.onerror', (e as unknown as { error?: unknown }).error ?? e);
      this.finish(null);
    };

    recorder.onstop = () => {
      // A recycled session produces no clip: drop what it captured and open a
      // new one on the same encoder. Done here rather than in restart() because
      // stop() flushes its last data asynchronously, and starting again before
      // that landed would file the finished session's tail under the new one.
      if (this.restarting) {
        this.restarting = false;
        this.chunks = [];
        this.beginSession();
        return;
      }

      // Before finish(), while the capture screen is still mounted and its
      // compositor still holds the frame the take ended on. Once the flow moves
      // to the result screen this canvas is gone.
      const posterUrl = posterFromCanvas(options.canvas);

      const blob = new Blob(this.chunks, { type: mimeType });
      this.chunks = [];
      if (blob.size === 0) {
        noteError('recorder', new Error('Recording produced an empty blob'));
        this.finish(null);
        return;
      }
      this.finish({
        blob,
        mimeType,
        durationMs: Date.now() - this.startedAt,
        sizeBytes: blob.size,
        posterUrl,
      });
    };

    this.beginSession();
    noteEvent(`recording started (${mimeType})`);
    return true;
  }

  /**
   * Open a recording session on the already-constructed encoder.
   *
   * Separate from start() because restart() comes back through here, and each
   * session needs its own clock and its own max-duration timer — a recorder
   * that has been recycled for a minute must still get its full window once it
   * is the one being kept.
   */
  private beginSession(): void {
    const recorder = this.recorder;
    if (!recorder) return;
    this.startedAt = Date.now();
    recorder.start(TIMESLICE_MS);
    this.clearMaxTimer();
    this.maxTimer = window.setTimeout(() => {
      noteEvent('recording hit max duration');
      void this.stop();
    }, this.maxDurationMs);
  }

  /**
   * Throw this session away and begin another on the same stream and encoder.
   *
   * Cheap on purpose: constructing a MediaRecorder and a canvas captureStream
   * is the expensive half (see arm()), and a rolling pre-roll recycles every
   * few hundred milliseconds — paying that each time would put the hitch it was
   * moved off the trigger frame to avoid right back into the live phase.
   */
  restart(): void {
    const recorder = this.recorder;
    if (!recorder || recorder.state === 'inactive') return;
    this.restarting = true;
    recorder.stop();
  }

  /** Wall clock at which the CURRENT session began; 0 when not recording. */
  get recordingSince(): number {
    return this.recording ? this.startedAt : 0;
  }

  /**
   * Mid-recycle: `stop()` has been called and the replacement session has not
   * opened yet.
   *
   * `stop()` flips MediaRecorder's state synchronously but `onstop` — and so
   * `beginSession()` — only runs a task turn later, which on a loaded board is
   * tens of milliseconds. Through that window `recordingSince` is 0 and the
   * recorder looks idle to anything choosing between slots.
   *
   * With two slots that never mattered: the other slot was always mid-cycle. On
   * the kiosk there is only one, so `PreRollRecorder.claim()` needs to be able
   * to tell "recycling, back imminently" apart from "not recording at all".
   */
  get reopening(): boolean {
    return this.restarting;
  }

  /**
   * Take over releasing the capture stream.
   *
   * Called when a shared stream's builder hands this recorder on and steps out
   * of the picture. Without it the canvas track outlives the session and every
   * visit leaks one — which is the failure that takes the kiosk down around
   * hour three.
   */
  assumeStreamOwnership(): void {
    this.ownsStream = true;
  }

  /** Resolves with the clip, or null if recording failed. Safe to call twice. */
  async stop(): Promise<Clip | null> {
    this.clearMaxTimer();
    if (!this.recorder) return this.result ?? null;
    if (this.recorder.state !== 'inactive') {
      this.recorder.stop();
    }
    return this.result ?? null;
  }

  get recording(): boolean {
    return this.recorder !== null && this.recorder.state === 'recording';
  }

  /** Abandon without producing a clip — session reset, idle timeout, retake. */
  cancel(): void {
    this.clearMaxTimer();
    this.restarting = false;
    if (this.recorder && this.recorder.state !== 'inactive') {
      this.recorder.onstop = null;
      this.recorder.ondataavailable = null;
      try {
        this.recorder.stop();
      } catch {
        /* already gone */
      }
    }
    this.chunks = [];
    this.finish(null);
  }

  private finish(clip: Clip | null): void {
    if (clip) noteEvent(`clip ready: ${(clip.sizeBytes / 1024 / 1024).toFixed(2)} MB`);
    this.settled?.(clip);
    this.settled = null;
    this.recorder = null;
    this.cleanupStream();
  }

  private cleanupStream(): void {
    // Only the canvas video track is ours to stop, and only when we opened the
    // stream ourselves. The mic stream is owned by the flow and reused across
    // retakes, so its track is left alone; a shared capture stream belongs to
    // whoever built it, until assumeStreamOwnership says otherwise.
    if (this.ownsStream) {
      this.stream?.getVideoTracks().forEach((t) => t.stop());
    }
    this.stream = null;
  }

  private clearMaxTimer(): void {
    if (this.maxTimer !== null) {
      clearTimeout(this.maxTimer);
      this.maxTimer = null;
    }
  }
}
